import styled from '@emotion/styled';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { type FC, type ReactNode, useEffect, useState } from 'react';

interface MapLegendProps {
  /** Legend content — compose rows with `LegendItem` / `LegendGroup` and the `getSymbol` helper. */
  children: ReactNode;
  /** Which top corner the legend anchors to. Defaults to `left`. */
  side?: 'left' | 'right';
  /** Panel heading. Defaults to `Legend`. */
  title?: string;
  /** Whether the panel starts expanded. Defaults to `true`. */
  defaultOpen?: boolean;
  /** Auto-close the panel after this many ms while open. `null` disables auto-close. Defaults to 30s. */
  autoCloseMs?: number | null;
  /** Distance from the top edge in px, e.g. to clear map controls sharing this side. Defaults to 10. */
  offsetTop?: number;
}

/**
 * Map legend shell: opens by default, auto-closes, morphs between panel and toggle button. Render
 * it as a direct child of `<BasicMap>` — it positions itself absolutely inside the map.
 */
export const MapLegend: FC<MapLegendProps> = ({
  children,
  side = 'left',
  title = 'Legend',
  defaultOpen = true,
  autoCloseMs = 30 * OneSecondInMillis,
  offsetTop = 10,
}) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);

  useEffect(() => {
    if (!open || autoCloseMs == null) return;
    const timer = setTimeout(() => setOpen(false), autoCloseMs);
    return () => clearTimeout(timer);
  }, [open, autoCloseMs]);

  return (
    <Container $side={side} $offsetTop={offsetTop}>
      {!open && (
        <ToggleButton
          type="button"
          onClick={() => setOpen(true)}
          title="Open map legend"
          aria-expanded={false}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
            <rect y="1" width="14" height="2" rx="1" />
            <rect y="6" width="14" height="2" rx="1" />
            <rect y="11" width="14" height="2" rx="1" />
          </svg>
        </ToggleButton>
      )}
      <Panel $open={open} $side={side} aria-hidden={!open}>
        <TitleRow>
          <Title>{title}</Title>
          <CloseButton
            type="button"
            onClick={() => setOpen(false)}
            title="Close legend"
            aria-label="Close legend"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </CloseButton>
        </TitleRow>
        {children}
      </Panel>
    </Container>
  );
};

/** A single legend row: a symbol/swatch plus its label (and optionally a trailing control). */
export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.78);

  code {
    font-size: 11px;
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
  }
`;

/** An uppercase section header grouping related rows. */
export const LegendGroup = styled.div`
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 8px;
  margin-bottom: 2px;

  &:first-of-type {
    margin-top: 2px;
  }
`;

/** A small filled circle swatch. */
export const LegendDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const Container = styled.div<{ $side: 'left' | 'right'; $offsetTop: number }>`
  position: absolute;
  top: ${({ $offsetTop }) => $offsetTop}px;
  ${({ $side }) => ($side === 'left' ? 'left: 10px;' : 'right: 10px;')}
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: ${({ $side }) => ($side === 'left' ? 'flex-start' : 'flex-end')};
  gap: 4px;
`;

const ToggleButton = styled.button`
  background: #fff;
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  width: 29px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.75);
  font-size: 14px;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #4096ff;
  }
`;

const Panel = styled.div<{ $open: boolean; $side: 'left' | 'right' }>`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 160px;
  transform-origin: ${({ $side }) => ($side === 'left' ? 'top left' : 'top right')};
  transform: ${({ $open }) => ($open ? 'scale(1)' : 'scale(0.05)')};
  opacity: ${({ $open }) => ($open ? '1' : '0')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  /* pointer-events alone leaves the close button and the switches in the tab order; visibility
     takes them out of it, delayed so the closing animation still plays. */
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: transform 0.2s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.15s ease,
    visibility 0s linear ${({ $open }) => ($open ? '0s' : '0.2s')};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  flex-shrink: 0;
  margin: -2px -2px -2px 4px;

  &:hover {
    color: rgba(0, 0, 0, 0.65);
    background: rgba(0, 0, 0, 0.06);
  }
`;
